import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Clock, AlertCircle } from "lucide-react";

function fmtDuration(ms) {
  if (ms <= 0) return "imminent";
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  } catch { return "—"; }
}

export default function NextScanCountdown() {
  const [info, setInfo] = useState(null);
  const [now, setNow] = useState(Date.now());

  // Re-fetch every 30s to stay accurate (in case backend just scanned)
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const r = await api.get("/strategy/next-scan");
        if (mounted) setInfo(r.data);
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  // Tick every second for live countdown
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!info) return null;

  const { next_scan_at, market_open, market_next_open, bot_enabled, last_scan_at, interval_min } = info;

  if (!bot_enabled) {
    return (
      <div className="panel p-4 flex items-center gap-3" data-testid="next-scan-widget">
        <AlertCircle size={16} className="text-[#FF3B30]" />
        <div>
          <div className="mono text-[10px] tracking-[0.22em] text-[#71717A]">BOT STATUS</div>
          <div className="mono text-sm mt-0.5 text-[#FF3B30]">DISARMED — no scans running</div>
        </div>
      </div>
    );
  }

  if (market_open === false) {
    const nextOpenMs = market_next_open ? new Date(market_next_open).getTime() - now : null;
    return (
      <div className="panel p-4 flex items-center gap-3" data-testid="next-scan-widget">
        <Clock size={16} className="text-[#FFD60A]" />
        <div className="flex-1">
          <div className="mono text-[10px] tracking-[0.22em] text-[#71717A]">MARKET CLOSED · BOT IDLE</div>
          <div className="mono text-sm mt-0.5">
            {nextOpenMs && nextOpenMs > 0 ? (
              <>NEXT SCAN IN <span className="text-[#FFD60A]">{fmtDuration(nextOpenMs)}</span> (at {fmtTime(market_next_open)})</>
            ) : (
              "Awaiting next market open"
            )}
          </div>
        </div>
      </div>
    );
  }

  const nextMs = next_scan_at ? new Date(next_scan_at).getTime() - now : null;
  const overdue = nextMs !== null && nextMs < 0;

  return (
    <div className="panel p-4 flex items-center gap-3" data-testid="next-scan-widget">
      <Clock size={16} className={overdue ? "text-[#00E676] animate-pulse" : "text-[#00E676]"} />
      <div className="flex-1">
        <div className="mono text-[10px] tracking-[0.22em] text-[#71717A]">
          MARKET OPEN · BOT SCANNING EVERY {interval_min}m
        </div>
        <div className="mono text-sm mt-0.5 flex items-baseline gap-3">
          {nextMs === null ? (
            <span>NEXT SCAN PENDING — bot will fire on next interval</span>
          ) : overdue ? (
            <span className="gain">SCAN IMMINENT · firing momentarily</span>
          ) : (
            <span>NEXT SCAN IN <span className="text-[#00E676]" data-testid="next-scan-countdown">{fmtDuration(nextMs)}</span></span>
          )}
          {last_scan_at && (
            <span className="text-[#71717A] text-xs">· last: {fmtTime(last_scan_at)}</span>
          )}
        </div>
      </div>
    </div>
  );
}
